// ---------------------------------------------------------------------------
// Table view — an EDITING surface (Monday-style): every cell changes the task
// in place via the same optimistic mutations the board uses. Spreadsheet-grade:
// cells are TEXT until touched — the real control mounts on click, autofocused,
// commits on change/blur/Enter, reverts on Esc. Headers sort within groups;
// the name column stays pinned under horizontal scroll.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { ApprovalState, Stage, TaskListItem } from '@cg-projects/shared';
import { Ban, ChevronDown, ChevronRight, ChevronUp, ListTree, Plus, Star } from 'lucide-react';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useTasksStore } from '@/features/tasks/store/tasksStore';
import { prefetchTask, useTaskDetailStore } from '@/features/task-detail/store/taskDetailStore';
import { useFilterStore } from '@/features/filters/store/filterStore';
import { useLookupStore } from '@/features/lookups/store/lookupStore';
import { filterTasks } from '@/features/tasks/filter';
import { groupTasks, sortTasks, taskDate, dueBucket, createDefaultsFor, type DateField, type GroupCreateDefaults } from '@/features/tasks/grouping';
import { moveStage, patchTask, closeWithUndo, createTask } from '@/features/tasks/mutations';
import { AssigneeStack, StateBadge, Skeleton, LoadError, fieldClassSm } from '@/core/ui/atoms';
import { Icon } from '@/core/ui/icons';
import { formatDate } from '@/core/ui/helpers';

// One grid for header + rows. The first track is the sticky unit: checkbox +
// priority star + name travel together, so bulk-select survives horizontal
// scroll along with the one column that identifies the row.
const COLS = 'grid grid-cols-[minmax(260px,1fr)_150px_170px_130px_120px] items-center gap-3';
// Same table without the date column, for a board that has declared its cards'
// dates past (Area.hideKeyDates — Content Calendar). A grid template and its
// cells must agree: drop the 130px track HERE and the header cell and the row
// cell below, or every column after the date slides one place left.
const COLS_NO_DATE = 'grid grid-cols-[minmax(260px,1fr)_150px_170px_120px] items-center gap-3';

const STATES: { value: ApprovalState; label: string }[] = [
  { value: '01_in_progress', label: 'In Progress' },
  { value: '02_changes_requested', label: 'Changes' },
  { value: '04_waiting_normal', label: 'Stuck' },
  { value: '03_approved', label: 'Approved' },
  { value: '1_done', label: 'Done' },
  { value: '1_canceled', label: 'Cancelled' },
];
const STATE_ORDER = new Map(STATES.map((s, i) => [s.value, i]));
// Badge only the states that SAY something (mirrors the board): "In Progress"
// and "Done" are the default condition of work — stamping every row is noise.
const NOTABLE_STATE = new Set<ApprovalState>(['02_changes_requested', '03_approved', '04_waiting_normal', '1_canceled']);

// Display face of an editable cell — quiet text with a hover affordance; the
// real control replaces it on click, so the table carries ~0 native controls
// at rest instead of ~4 × every row.
const cellBtn = 'block w-full truncate rounded px-1 py-0.5 text-left text-xs transition hover:bg-hover';

// Sticky cells must be OPAQUE — the row tints are translucent overlays, and
// content sliding beneath a see-through pinned cell reads as a glitch. These
// mixes reproduce hover:bg-hover/50 and bg-brand/5 composited over the page.
const STICKY_HOVER = 'group-hover:[background:color-mix(in_srgb,var(--color-hover)_50%,var(--color-page))]';
const STICKY_BASE = `bg-page ${STICKY_HOVER}`;
const STICKY_SELECTED = `[background:color-mix(in_srgb,var(--color-brand)_5%,var(--color-page))] ${STICKY_HOVER}`;

// Hover-intent prefetch (instant-open layer 3): 100ms of dwell on a row warms
// the task-detail cache so opening the task renders with no spinner. One timer
// is enough — the pointer only hovers one row at a time. Touch-only devices
// never produce a useful mouseenter, so they skip the wiring entirely. (The
// typeof guard keeps this module importable in the Node unit-test runner.)
const HOVERABLE = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;
let hoverTimer: ReturnType<typeof setTimeout> | undefined;
const hoverStart = (id: number) => {
  if (!HOVERABLE) return;
  clearTimeout(hoverTimer);
  hoverTimer = setTimeout(() => prefetchTask(id), 100);
};
const hoverEnd = () => clearTimeout(hoverTimer);

// ---- Header sort (persisted) ----------------------------------------------
type SortCol = 'priority' | 'name' | 'stage' | 'assignees' | 'date' | 'state';
type TableSort = { col: SortCol; dir: 'asc' | 'desc' };
type EditCol = 'stage' | 'assignee' | 'date' | 'state';
const SORT_KEY = 'cgp:table:sort';
const SORT_COLS: SortCol[] = ['priority', 'name', 'stage', 'assignees', 'date', 'state'];

function loadSort(): TableSort | null {
  try {
    const v = JSON.parse(localStorage.getItem(SORT_KEY) ?? 'null') as TableSort | null;
    if (v && SORT_COLS.includes(v.col) && (v.dir === 'asc' || v.dir === 'desc')) return v;
  } catch { /* no storage / bad JSON — fall back to the app's default order */ }
  return null;
}
function saveSort(s: TableSort | null) {
  try {
    if (s) localStorage.setItem(SORT_KEY, JSON.stringify(s));
    else localStorage.removeItem(SORT_KEY);
  } catch { /* storage unavailable — the choice just won't survive a reload */ }
}

// Sorts WITHIN each group so grouping stays the primary read. Empty values
// (no date, unassigned, unknown stage) sink to the bottom in BOTH directions —
// missing data is not a large value. Ties fall back to the manual order.
function compareTasks(
  a: TaskListItem,
  b: TaskListItem,
  sort: TableSort,
  dateField: DateField,
  stageById: Map<number, { name: string; order: number }>,
): number {
  const dir = sort.dir === 'asc' ? 1 : -1;
  const tie = a.sequence - b.sequence || a.id - b.id;
  switch (sort.col) {
    case 'name':
      return dir * a.name.localeCompare(b.name) || tie;
    case 'priority':
      // asc = high first — the read a star column gets sorted for.
      return dir * ((a.priority === '1' ? 0 : 1) - (b.priority === '1' ? 0 : 1)) || tie;
    case 'stage': {
      const x = stageById.get(a.stageId)?.order;
      const y = stageById.get(b.stageId)?.order;
      if (x === undefined || y === undefined) return x === y ? tie : x === undefined ? 1 : -1;
      return dir * (x - y) || tie;
    }
    case 'date': {
      // ISO dates compare correctly as text — no Date parsing needed.
      const x = taskDate(a, dateField);
      const y = taskDate(b, dateField);
      if (!x || !y) return x === y ? tie : !x ? 1 : -1;
      return dir * x.localeCompare(y) || tie;
    }
    case 'assignees': {
      const x = a.assignees[0]?.name;
      const y = b.assignees[0]?.name;
      if (!x || !y) return x === y ? tie : !x ? 1 : -1;
      return dir * x.localeCompare(y) || tie;
    }
    case 'state':
      return dir * ((STATE_ORDER.get(a.state) ?? 99) - (STATE_ORDER.get(b.state) ?? 99)) || tie;
  }
}

// Mount-time ref for the swapped-in control: focus it and pop its native
// picker inside the same click's user activation. Module-level so the ref
// identity is stable — an inline closure would re-run on every keystroke
// re-render and keep re-opening the picker.
function openPicker(el: HTMLSelectElement | HTMLInputElement | null) {
  if (!el) return;
  el.focus();
  try {
    (el as HTMLElement & { showPicker?: () => void }).showPicker?.();
  } catch { /* older browser or lost activation — focus alone is fine */ }
}

function SortHeader({
  label,
  title,
  col,
  sort,
  onSort,
  className = '',
}: {
  label: ReactNode;
  title: string;
  col: SortCol;
  sort: TableSort | null;
  onSort: (col: SortCol) => void;
  className?: string;
}) {
  const active = sort?.col === col;
  return (
    <button
      onClick={() => onSort(col)}
      title={title}
      className={`flex min-w-0 items-center gap-0.5 text-left transition ${active ? 'text-brand' : 'hover:text-ink'} ${className}`}
    >
      {typeof label === 'string' ? <span className="truncate">{label}</span> : label}
      {sort?.col === col && <Icon i={sort.dir === 'asc' ? ChevronUp : ChevronDown} size={14} />}
    </button>
  );
}

// The foot of every group that can honestly hold a new task (a stage, a person,
// high priority, due today, no date): "+ Add task", typed in place. Until
// v1.93.0 the table had no way to add at all — Matt Riley read that as having
// no access to Merchandise (9/25). The group's own values ride along so the new
// row lands in the group it was typed under. Stays open for rapid adds, like
// the board's composer; Esc or an empty blur closes it.
function AddTaskRow({ areaId, defaults, cols }: { areaId: number; defaults: GroupCreateDefaults; cols: string }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  const submit = async () => {
    const n = name.trim();
    if (!n || busy) return;
    setBusy(true);
    const task = await createTask({ areaId, name: n, ...defaults });
    setBusy(false);
    if (task) { setName(''); inputRef.current?.focus(); }
  };

  return (
    <div className={`${cols} h-9 border-b border-line pr-4`}>
      <div className={`sticky left-0 z-[1] flex h-full min-w-0 items-center pl-4 pr-1 ${STICKY_BASE}`}>
        {open ? (
          <input
            ref={inputRef}
            value={name}
            // Never `disabled` while saving: a disabled input drops focus, so the
            // next name would type into nothing. submit() already ignores repeats.
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); void submit(); }
              else if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); setName(''); }
            }}
            onBlur={() => { if (!name.trim() && !busy) setOpen(false); }}
            placeholder="Task name — Enter to add, Esc to close"
            aria-label="New task name"
            className={`${fieldClassSm} w-full`}
          />
        ) : (
          <button
            onClick={() => setOpen(true)}
            className="flex items-center gap-1 rounded px-1 py-0.5 text-xs text-faint transition hover:bg-hover hover:text-ink"
          >
            <Icon i={Plus} size={14} /> Add task
          </button>
        )}
      </div>
    </div>
  );
}

export function TableView({ areaId }: { areaId: number }) {
  const area = useAreaStore((s) => s.areas.find((a) => a.id === areaId));
  const stages = useAreaStore((s) => s.stagesByArea[areaId]) as Stage[] | undefined;
  const loadStages = useAreaStore((s) => s.loadStages);
  const tasks = useTasksStore((s) => s.tasksByArea[areaId]);
  const loading = useTasksStore((s) => s.loadingArea[areaId]);
  // Board subscribed to this; Table didn't — so a failed load here rendered the
  // "No tasks match." empty state and the user's work looked deleted.
  const error = useTasksStore((s) => s.errorByArea[areaId]);
  const loadTasks = useTasksStore((s) => s.loadTasks);
  const open = useTaskDetailStore((s) => s.open);
  const lookupAssignees = useLookupStore((s) => s.assignees);
  const loadLookups = useLookupStore((s) => s.load);

  const { search, assigneeId, tagId, stageId, onlyUnassigned, dueThisWeek, showCompleted, groupBy, sortBy } = useFilterStore();
  const dateField: DateField = area?.calendarDateField ?? 'date_deadline';
  // The board says whether its cards still have a shoot/delivery date worth
  // showing. Content Calendar says no — the video is shot and posted.
  const hideDates = Boolean(area?.hideKeyDates);
  const cols = hideDates ? COLS_NO_DATE : COLS;

  const selectedIds = useTasksStore((s) => s.selectedIds);
  const toggleSelected = useTasksStore((s) => s.toggleSelected);
  const setSelected = useTasksStore((s) => s.setSelected);
  const selectRange = useTasksStore((s) => s.selectRange);

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [tableSort, setTableSort] = useState<TableSort | null>(loadSort);
  // One cell edits at a time, spreadsheet-style; the date edit holds a draft
  // so Esc can walk away without a write.
  const [editing, setEditing] = useState<{ id: number; col: EditCol } | null>(null);
  const [dateDraft, setDateDraft] = useState('');

  useEffect(() => { void loadStages(areaId); }, [areaId, loadStages]);
  useEffect(() => { void loadTasks(areaId, showCompleted); }, [areaId, showCompleted, loadTasks]);
  useEffect(() => { void loadLookups(); }, [loadLookups]);

  const stageById = useMemo(() => {
    const m = new Map<number, { name: string; order: number }>();
    (stages ?? []).forEach((s, i) => m.set(s.id, { name: s.name, order: i }));
    return m;
  }, [stages]);

  const groups = useMemo(() => {
    const visible = filterTasks(tasks ?? [], { search, assigneeId, tagId, onlyUnassigned, dueThisWeek, stageId, dateField });
    return groupTasks(visible, groupBy, { stages: stages ?? [], dateField }).map((g) => ({
      ...g,
      // A header sort overrides the toolbar sort WITHIN each group; grouping
      // stays the primary read either way.
      tasks: tableSort
        ? [...g.tasks].sort((a, b) => compareTasks(a, b, tableSort, dateField, stageById))
        : sortTasks(g.tasks, sortBy, dateField),
    }));
  }, [tasks, stages, stageById, groupBy, sortBy, tableSort, search, assigneeId, tagId, stageId, onlyUnassigned, dueThisWeek, dateField]);

  const foldKey = `${groupBy}:${groups.map((g) => g.key).join(',')}`;
  useEffect(() => {
    setCollapsed(new Set(groups.filter((g) => g.fold).map((g) => g.key)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foldKey]);

  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  const onSort = (col: SortCol) =>
    setTableSort((prev) => {
      // asc → desc → back to the app's default order; a sort you can't leave
      // would permanently eat manual ordering.
      const next: TableSort | null =
        prev?.col !== col ? { col, dir: 'asc' } : prev.dir === 'asc' ? { col, dir: 'desc' } : null;
      saveSort(next);
      return next;
    });

  const commitDate = (t: TaskListItem, current: string) => {
    setEditing(null);
    const v = dateDraft || null;
    if (v === (current || null)) return; // untouched — no write
    if (dateField === 'film_date') void patchTask(t.id, { filmDate: v }, { filmDate: v });
    else void patchTask(t.id, { deadline: v }, { deadline: v });
  };

  if (error) return <LoadError message={error} onRetry={() => void loadTasks(areaId, showCompleted, true)} />;
  if (loading && !tasks) {
    // Boot ghost — a header bar over 8 rows in the table's real COLS grid and
    // row rhythm, so the swap to live rows doesn't jump.
    return (
      <div aria-hidden className="h-full overflow-auto">
        <div className="min-w-[900px]">
          <div className={`${cols} h-8 border-b border-line pr-4`}>
            <div className="flex items-center gap-2 pl-4">
              <Skeleton className="h-3.5 w-3.5" />
              <Skeleton className="h-3 w-24" />
            </div>
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-20" />
            {!hideDates && <Skeleton className="h-3 w-16" />}
            <Skeleton className="h-3 w-14" />
          </div>
          {[0, 1, 2, 3, 4, 5, 6, 7].map((row) => (
            <div key={row} className={`${cols} h-9 border-b border-line pr-4`}>
              <div className="flex items-center gap-2 pl-4">
                <Skeleton className="h-3.5 w-3.5" />
                <Skeleton className="h-4 w-3/4" />
              </div>
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-[22px] w-14 rounded-full" />
              {!hideDates && <Skeleton className="h-4 w-20" />}
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const total = groups.reduce((n, g) => n + g.tasks.length, 0);
  // "Select all visible" means VISIBLE — collapsed groups (Done/Archive fold
  // shut by default) stay out, or a bulk shift would edit rows nobody saw.
  // Order matches the rendered rows, so shift-click ranges span what the eye
  // sees, sort and all.
  const allVisible = groups.filter((g) => !collapsed.has(g.key)).flatMap((g) => g.tasks.map((t) => t.id));
  const dateLabel = dateField === 'film_date' ? (area?.keyDateLabel ?? 'Key date') : 'Deadline';

  return (
    // ONE scroll container for BOTH axes — sticky-left cells and sticky-top
    // headers must share a scrollport or the pins silently stop working.
    <div className="h-full overflow-auto">
      <div className="min-w-[900px]">
        <div className={`${cols} sticky top-0 z-20 h-8 border-b border-line bg-page pr-4 text-2xs font-semibold uppercase tracking-wide text-faint`}>
          <div className="sticky left-0 z-[1] flex h-full min-w-0 items-center gap-1 bg-page pl-4">
            <input
              type="checkbox"
              checked={allVisible.length > 0 && allVisible.every((id) => selectedIds.includes(id))}
              onChange={(e) => setSelected(e.target.checked ? allVisible : [])}
              title="Select all visible"
              className="mr-1 h-3.5 w-3.5 shrink-0 accent-brand"
            />
            <SortHeader label={<Icon i={Star} size={14} />} title="Sort by priority" col="priority" sort={tableSort} onSort={onSort} className="shrink-0" />
            <SortHeader label="Name" title="Sort by name" col="name" sort={tableSort} onSort={onSort} className="min-w-0 flex-1" />
          </div>
          <SortHeader label="Stage" title="Sort by stage" col="stage" sort={tableSort} onSort={onSort} />
          <SortHeader label="Assignees" title="Sort by assignee" col="assignees" sort={tableSort} onSort={onSort} />
          {!hideDates && <SortHeader label={dateLabel} title={`Sort by ${dateLabel.toLowerCase()}`} col="date" sort={tableSort} onSort={onSort} />}
          <SortHeader label="Status" title="Sort by status" col="state" sort={tableSort} onSort={onSort} />
        </div>

        {groups.map((g) => {
          const isCollapsed = collapsed.has(g.key);
          const addDefaults = createDefaultsFor(g.key, { stages: stages ?? [], dateField });
          return (
            <div key={g.key}>
              <button
                onClick={() => toggle(g.key)}
                className="sticky top-8 z-10 flex w-full items-center border-b border-line bg-page px-4 py-1.5 text-left text-xs font-semibold text-ink hover:bg-hover"
              >
                {/* The label stays readable while the wide grid scrolls under it */}
                <span className="sticky left-4 flex min-w-0 items-center gap-2">
                  <Icon i={isCollapsed ? ChevronRight : ChevronDown} size={14} className="text-faint" />
                  <span className="truncate">{g.label}</span>
                  <span className="rounded bg-hover px-1.5 text-2xs font-normal text-muted">{g.count}</span>
                </span>
              </button>
              {!isCollapsed &&
                g.tasks.map((t) => {
                  const selected = selectedIds.includes(t.id);
                  const dateValue = (dateField === 'film_date' ? t.filmDate : t.deadline) ?? '';
                  const bucket = dueBucket(dateValue || null);
                  const dColor = bucket === 'overdue' ? 'text-danger' : bucket === 'today' || bucket === 'week' ? 'text-warn' : 'text-muted';
                  const high = t.priority === '1';
                  const isEdit = (col: EditCol) => editing?.id === t.id && editing.col === col;
                  const addable = lookupAssignees.filter((a) => !t.assignees.some((x) => x.id === a.id));
                  return (
                    <div
                      key={t.id}
                      onMouseEnter={() => hoverStart(t.id)}
                      onMouseLeave={hoverEnd}
                      className={`${cols} group h-9 border-b border-line pr-4 text-sm text-ink hover:bg-hover/50 ${selected ? 'bg-brand/5' : ''}`}
                    >
                      {/* Pinned: bulk-select + priority + name — the row's identity */}
                      <div
                        className={`sticky left-0 z-[1] flex h-full min-w-0 items-center gap-1 border-l-2 pl-3.5 pr-1 ${high ? 'border-l-warn' : 'border-l-transparent'} ${selected ? STICKY_SELECTED : STICKY_BASE}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          // Selection lives in onClick so shift is readable; the
                          // no-op onChange keeps the controlled-input contract.
                          onChange={() => {}}
                          onClick={(e) => {
                            const anchor = useTasksStore.getState().lastSelectedId;
                            if (e.shiftKey && anchor != null) selectRange(anchor, t.id, allVisible);
                            else toggleSelected(t.id);
                          }}
                          className="mr-1 h-3.5 w-3.5 shrink-0 accent-brand"
                        />
                        {/* Star only when it means something; the affordance
                            surfaces on row hover (and keyboard focus) */}
                        <button
                          onClick={() => void patchTask(t.id, { priority: high ? '0' : '1' }, { priority: high ? '0' : '1' })}
                          title={high ? 'High priority — click to clear' : 'Mark high priority'}
                          className={`shrink-0 px-0.5 ${high ? 'text-warn' : 'text-faint opacity-0 transition-opacity hover:text-warn focus-visible:opacity-60 group-hover:opacity-60'}`}
                        >
                          <Icon i={Star} size={14} className={high ? 'fill-current' : ''} />
                        </button>
                        <button onClick={() => open(t.id)} className="min-w-0 flex-1 truncate text-left hover:underline" title="Open task">
                          {t.name}
                        </button>
                        {t.childCount > 0 && (
                          <span
                            className={`flex shrink-0 items-center gap-0.5 text-2xs ${t.childDoneCount >= t.childCount ? 'text-brand' : 'text-faint'}`}
                            title="Sub-tasks done"
                          >
                            <Icon i={ListTree} size={14} />
                            {t.childDoneCount}/{t.childCount}
                          </span>
                        )}
                        {t.blockedByCount > 0 && (
                          <span className="shrink-0 text-danger" title="Blocked">
                            <Icon i={Ban} size={14} />
                          </span>
                        )}
                      </div>

                      {/* Stage: name as text; the select mounts on click */}
                      {isEdit('stage') ? (
                        <select
                          ref={openPicker}
                          value={t.stageId}
                          onChange={(e) => { setEditing(null); void moveStage(t.id, Number(e.target.value)); }}
                          onBlur={() => setEditing(null)}
                          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setEditing(null); } }}
                          className={fieldClassSm}
                        >
                          {(stages ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                          {stages && !stages.some((s) => s.id === t.stageId) && <option value={t.stageId}>#{t.stageId}</option>}
                        </select>
                      ) : (
                        <button onClick={() => setEditing({ id: t.id, col: 'stage' })} className={`${cellBtn} text-ink`} title="Change stage">
                          {stageById.get(t.stageId)?.name ?? `#${t.stageId}`}
                        </button>
                      )}

                      {/* Assignees: avatars; click for the picker it already had */}
                      {isEdit('assignee') ? (
                        <span className="flex min-w-0 items-center gap-1">
                          <AssigneeStack assignees={t.assignees} max={3} />
                          <select
                            ref={openPicker}
                            value=""
                            onChange={(e) => {
                              setEditing(null);
                              const a = lookupAssignees.find((x) => x.id === Number(e.target.value));
                              if (a) void patchTask(t.id, { assigneeIds: [...t.assignees.map((x) => x.id), a.id] }, { assignees: [...t.assignees, a] });
                            }}
                            onBlur={() => setEditing(null)}
                            onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setEditing(null); } }}
                            className={`${fieldClassSm} min-w-0 flex-1`}
                          >
                            <option value="">Add…</option>
                            {addable.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                          </select>
                        </span>
                      ) : (
                        <button
                          onClick={() => { if (addable.length > 0) setEditing({ id: t.id, col: 'assignee' }); }}
                          className="flex h-6 w-full min-w-0 items-center rounded px-1 transition hover:bg-hover"
                          title={addable.length > 0 ? 'Add assignee' : undefined}
                        >
                          {t.assignees.length > 0 ? <AssigneeStack assignees={t.assignees} max={3} /> : <span className="text-xs text-faint">—</span>}
                        </button>
                      )}

                      {/* Date: formatted text (em-dash when empty — NEVER a bare
                          native input's mm/dd/yyyy); the picker mounts on click
                          and holds a draft so Esc reverts. Absent entirely on a
                          board that hides its dates — its header track is gone too. */}
                      {hideDates ? null : isEdit('date') ? (
                        <input
                          ref={openPicker}
                          type="date"
                          value={dateDraft}
                          onChange={(e) => setDateDraft(e.target.value)}
                          onBlur={() => commitDate(t, dateValue.slice(0, 10))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                            else if (e.key === 'Escape') { e.stopPropagation(); setEditing(null); }
                          }}
                          className={fieldClassSm}
                        />
                      ) : (
                        <button
                          onClick={() => { setDateDraft(dateValue.slice(0, 10)); setEditing({ id: t.id, col: 'date' }); }}
                          className={`${cellBtn} tabular-nums ${dateValue ? dColor : 'text-faint'}`}
                          title={`Set ${dateLabel.toLowerCase()}`}
                        >
                          {dateValue ? formatDate(dateValue) : '—'}
                        </button>
                      )}

                      {/* State: notable-only badge; the select mounts on click */}
                      {isEdit('state') ? (
                        <select
                          ref={openPicker}
                          value={t.state}
                          onChange={(e) => {
                            setEditing(null);
                            const next = e.target.value as ApprovalState;
                            if (next === t.state) return;
                            // Stuck needs a reason — route through the task page's flow.
                            if (next === '04_waiting_normal') { void open(t.id); return; }
                            // Closing hides the row — same Undo the ✓ button gives,
                            // so the row can't just vanish on you with no way back.
                            if (next === '1_done' || next === '1_canceled') {
                              void closeWithUndo(t.id, t.name, t.state, next);
                              return;
                            }
                            void patchTask(t.id, { state: next }, { state: next });
                          }}
                          onBlur={() => setEditing(null)}
                          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setEditing(null); } }}
                          className={fieldClassSm}
                        >
                          {STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      ) : (
                        <button onClick={() => setEditing({ id: t.id, col: 'state' })} className={cellBtn} title="Change status">
                          {NOTABLE_STATE.has(t.state) ? <StateBadge state={t.state} /> : <span className="text-faint">—</span>}
                        </button>
                      )}
                    </div>
                  );
                })}
              {!isCollapsed && addDefaults && <AddTaskRow areaId={areaId} defaults={addDefaults} cols={cols} />}
            </div>
          );
        })}
        {total === 0 && <div className="px-4 py-6 text-sm text-faint">No tasks match.</div>}
      </div>
    </div>
  );
}
